import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";

import {
  isInvalidAuthStateError,
  isSsoDomainDenyError,
  parseSsoDomainDenyEmail,
} from "@/utils/ssoDomainLoginRecovery";

/**
 * When a social login completes on an SSO-backed domain, the Sync User action
 * denies with domain_requires_sso:{email}. Restart login with login_hint so ACUL
 * can route through HRD without breaking the SPA's PKCE transaction.
 *
 * Also recovers from invalid-state errors left over from the old /authorize
 * redirect approach.
 */
export function useSsoDomainLoginRecovery(): boolean {
  const { error, loginWithRedirect } = useAuth0();
  const [recovering, setRecovering] = useState(false);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) {
      return;
    }

    const restartWithLoginHint = (email: string) => {
      attemptedRef.current = true;
      setRecovering(true);
      void loginWithRedirect({
        authorizationParams: {
          login_hint: email,
          prompt: "login",
        },
      });
    };

    const restartLogin = () => {
      attemptedRef.current = true;
      setRecovering(true);
      void loginWithRedirect({
        authorizationParams: {
          prompt: "login",
        },
      });
    };

    const params = new URLSearchParams(window.location.search);
    if (params.get("error") === "access_denied") {
      const description = params.get("error_description") ?? "";
      if (isSsoDomainDenyError(description)) {
        const email = parseSsoDomainDenyEmail(description);
        if (email) {
          restartWithLoginHint(email);
          return;
        }
      }
    }

    if (!error) {
      return;
    }

    const errorText = error.message ?? "";
    if (isSsoDomainDenyError(errorText)) {
      const email = parseSsoDomainDenyEmail(errorText);
      if (email) {
        restartWithLoginHint(email);
        return;
      }
    }

    if (isInvalidAuthStateError(errorText)) {
      restartLogin();
    }
  }, [error, loginWithRedirect]);

  return recovering;
}
